import type { Rng } from '@/lib/cloud/rng'
import type {
    Budget,
    FramePose,
    Rgb,
    ShapeCamera,
    ShapeData,
    Vec3
} from '@/lib/cloud/types'

import { PALETTE } from '@/lib/cloud/palette'
import {
    budgetTotal,
    categorySizes,
    categoryStarts,
    KIND_GROUND
} from '@/lib/cloud/types'

export interface BuildParams<TExtra> {
    key: string
    anchors: Record<string, Vec3>
    frame: FramePose
    camera: ShapeCamera
    radius: number
    height: number
    extra?: TExtra
}

/**
 * Fills four fixed-size point categories (ground, structure, hot, live) with styled points.
 * Every category always ends up exactly full, so shapes built from the same budget share indices.
 */
export class CloudBuilder {
    readonly budget: Budget
    readonly count: number
    readonly positions: Float32Array
    readonly colors: Uint8Array
    readonly meta: Float32Array
    readonly rng: Rng

    private readonly starts: Int32Array
    private readonly ends: Int32Array
    private readonly cursors: Int32Array
    private category = KIND_GROUND

    private r = PALETTE.point[0]
    private g = PALETTE.point[1]
    private b = PALETTE.point[2]
    private a = 0.55
    private size = 1
    private pulse = 0
    private spread = 0.012

    constructor(budget: Budget, rng: Rng) {
        this.budget = budget
        this.rng = rng
        this.count = budgetTotal(budget)
        this.positions = new Float32Array(this.count * 3)
        this.colors = new Uint8Array(this.count * 4)
        this.meta = new Float32Array(this.count * 2)

        const starts = categoryStarts(budget)
        const sizes = categorySizes(budget)

        this.starts = new Int32Array(starts)
        this.ends = new Int32Array(starts.map((start, index) => start + sizes[index]))
        this.cursors = new Int32Array(starts)
    }

    use(category: number): this {
        this.category = category

        return this
    }

    get remaining(): number {
        return this.ends[this.category] - this.cursors[this.category]
    }

    capacity(category: number): number {
        return this.ends[category] - this.starts[category]
    }

    style(color: Rgb, alpha: number, size: number = 1, pulse: number = 0): this {
        this.r = color[0]
        this.g = color[1]
        this.b = color[2]
        this.a = alpha
        this.size = size
        this.pulse = pulse

        return this
    }

    jitter(amount: number): this {
        this.spread = amount

        return this
    }

    point(x: number, y: number, z: number): boolean {
        const c = this.category
        const i = this.cursors[c]

        if (i >= this.ends[c]) return false

        this.cursors[c] = i + 1

        const s = this.spread
        const rng = this.rng
        const p = i * 3

        this.positions[p] = x + (rng() - 0.5) * 2 * s
        this.positions[p + 1] = y + (rng() - 0.5) * 2 * s
        this.positions[p + 2] = z + (rng() - 0.5) * 2 * s

        const q = i * 4

        this.colors[q] = Math.round(Math.min(1, Math.max(0, this.r)) * 255)
        this.colors[q + 1] = Math.round(Math.min(1, Math.max(0, this.g)) * 255)
        this.colors[q + 2] = Math.round(Math.min(1, Math.max(0, this.b)) * 255)
        this.colors[q + 3] = Math.round(Math.min(1, Math.max(0, this.a)) * 255)

        this.meta[i * 2] = this.size
        this.meta[i * 2 + 1] = this.pulse

        return true
    }

    paint(x: number, y: number, z: number, color: Rgb, alpha: number, size: number = this.size, pulse: number = 0): boolean {
        const keep: [number, number, number, number, number, number] = [this.r, this.g, this.b, this.a, this.size, this.pulse]

        this.r = color[0]
        this.g = color[1]
        this.b = color[2]
        this.a = alpha
        this.size = size
        this.pulse = pulse

        const placed = this.point(x, y, z)

        this.r = keep[0]
        this.g = keep[1]
        this.b = keep[2]
        this.a = keep[3]
        this.size = keep[4]
        this.pulse = keep[5]

        return placed
    }

    line(ax: number, ay: number, az: number, bx: number, by: number, bz: number, n: number, even: boolean = false): void {
        for (let k = 0; k < n; k++) {
            const t = even ? (n === 1 ? 0.5 : k / (n - 1)) : this.rng()

            if (!this.point(ax + (bx - ax) * t, ay + (by - ay) * t, az + (bz - az) * t)) return
        }
    }

    quad(
        ox: number,
        oy: number,
        oz: number,
        ux: number,
        uy: number,
        uz: number,
        vx: number,
        vy: number,
        vz: number,
        n: number
    ): void {
        for (let k = 0; k < n; k++) {
            const s = this.rng()
            const t = this.rng()

            if (!this.point(ox + ux * s + vx * t, oy + uy * s + vy * t, oz + uz * s + vz * t)) return
        }
    }

    box(minX: number, minY: number, minZ: number, maxX: number, maxY: number, maxZ: number, n: number): void {
        const dx = maxX - minX
        const dy = maxY - minY
        const dz = maxZ - minZ
        const perimeter = 4 * (dx + dy + dz)

        if (perimeter <= 0) return

        for (let k = 0; k < n; k++) {
            let s = this.rng() * perimeter
            let x = minX
            let y = minY
            let z = minZ
            const corner = Math.floor(this.rng() * 4)
            const cA = corner & 1
            const cB = (corner >> 1) & 1

            if (s < 4 * dx) {
                x = minX + (s % dx)
                y = cA ? maxY : minY
                z = cB ? maxZ : minZ
            }
            else if ((s -= 4 * dx) < 4 * dy) {
                y = minY + (s % dy)
                x = cA ? maxX : minX
                z = cB ? maxZ : minZ
            }
            else {
                s -= 4 * dy
                z = minZ + (s % dz)
                x = cA ? maxX : minX
                y = cB ? maxY : minY
            }

            if (!this.point(x, y, z)) return
        }
    }

    prism(ax: number, ay: number, az: number, bx: number, by: number, bz: number, width: number, height: number, n: number): void {
        const dx = bx - ax
        const dy = by - ay
        const dz = bz - az
        const horizontal = Math.hypot(dx, dz)

        let px = 1
        let pz = 0

        if (horizontal > 1e-6) {
            px = -dz / horizontal
            pz = dx / horizontal
        }

        const length = Math.hypot(dx, dy, dz) || 1
        const qx = -(pz * dy) / length
        const qy = (pz * dx - px * dz) / length
        const qz = (px * dy) / length
        const perimeter = 2 * (width + height)

        for (let k = 0; k < n; k++) {
            const t = this.rng()
            let s = this.rng() * perimeter
            let u = 0
            let v = 0

            if (s < width) {
                u = s - width / 2
                v = -height / 2
            }
            else if ((s -= width) < height) {
                u = width / 2
                v = s - height / 2
            }
            else if ((s -= height) < width) {
                u = width / 2 - s
                v = height / 2
            }
            else {
                s -= width
                u = -width / 2
                v = height / 2 - s
            }

            const x = ax + dx * t + px * u + qx * v
            const y = ay + dy * t + qy * v
            const z = az + dz * t + pz * u + qz * v

            if (!this.point(x, y, z)) return
        }
    }

    post(x: number, z: number, y0: number, y1: number, width: number, n: number): void {
        const half = width / 2

        for (let k = 0; k < n; k++) {
            const y = y0 + (y1 - y0) * this.rng()
            const s = this.rng() * 4
            const side = Math.floor(s)
            const f = (s - side) * width - half

            let px = x
            let pz = z

            if (side === 0) {
                px = x + f
                pz = z - half
            }
            else if (side === 1) {
                px = x + half
                pz = z + f
            }
            else if (side === 2) {
                px = x - f
                pz = z + half
            }
            else {
                px = x - half
                pz = z - f
            }

            if (!this.point(px, y, pz)) return
        }
    }

    outline(
        cx: number,
        cy: number,
        cz: number,
        ux: number,
        uy: number,
        uz: number,
        vx: number,
        vy: number,
        vz: number,
        halfU: number,
        halfV: number,
        n: number
    ): void {
        const perimeter = 4 * (halfU + halfV)

        for (let k = 0; k < n; k++) {
            let s = this.rng() * perimeter
            let a = 0
            let b = 0

            if (s < 2 * halfU) {
                a = s - halfU
                b = -halfV
            }
            else if ((s -= 2 * halfU) < 2 * halfV) {
                a = halfU
                b = s - halfV
            }
            else if ((s -= 2 * halfV) < 2 * halfU) {
                a = halfU - s
                b = halfV
            }
            else {
                s -= 2 * halfU
                a = -halfU
                b = halfV - s
            }

            if (!this.point(cx + ux * a + vx * b, cy + uy * a + vy * b, cz + uz * a + vz * b)) return
        }
    }

    ring(cx: number, cy: number, cz: number, radius: number, n: number, even: boolean = false): void {
        for (let k = 0; k < n; k++) {
            const angle = even ? (k / n) * Math.PI * 2 : this.rng() * Math.PI * 2

            if (!this.point(cx + Math.cos(angle) * radius, cy, cz + Math.sin(angle) * radius)) return
        }
    }

    private pad(category: number): void {
        const start = this.starts[category]
        const filled = this.cursors[category] - start

        this.category = category

        while (this.cursors[category] < this.ends[category]) {
            const i = this.cursors[category]

            this.cursors[category] = i + 1

            if (filled > 0) {
                const src = start + Math.floor(this.rng() * filled)

                this.positions[i * 3] = this.positions[src * 3] + (this.rng() - 0.5) * 0.06
                this.positions[i * 3 + 1] = this.positions[src * 3 + 1] + (this.rng() - 0.5) * 0.06
                this.positions[i * 3 + 2] = this.positions[src * 3 + 2] + (this.rng() - 0.5) * 0.06
                this.colors[i * 4] = this.colors[src * 4]
                this.colors[i * 4 + 1] = this.colors[src * 4 + 1]
                this.colors[i * 4 + 2] = this.colors[src * 4 + 2]
                this.colors[i * 4 + 3] = this.colors[src * 4 + 3]
                this.meta[i * 2] = this.meta[src * 2]
                this.meta[i * 2 + 1] = this.meta[src * 2 + 1]
            }
            else {
                this.positions[i * 3] = 0
                this.positions[i * 3 + 1] = 0
                this.positions[i * 3 + 2] = 0
                this.colors[i * 4 + 3] = 0
                this.meta[i * 2] = 0
                this.meta[i * 2 + 1] = 0
            }
        }
    }

    build<TExtra>(params: BuildParams<TExtra>): ShapeData<TExtra> {
        for (let c = 0; c < 4; c++) this.pad(c)

        return {
            key: params.key,
            budget: this.budget,
            count: this.count,
            positions: this.positions,
            colors: this.colors,
            meta: this.meta,
            anchors: params.anchors,
            frame: params.frame,
            camera: params.camera,
            radius: params.radius,
            height: params.height,
            extra: params.extra
        }
    }
}
